import { Link } from "react-router-dom";
import { Heart, MapPin, Calendar, Gauge, Zap, Gavel, Coins, GitCompareArrows, ShieldCheck, Star, MessageSquare, Target, Info, Flame, Archive } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "./ui/badge";
import { formatPrice, formatMileage } from "../lib/api";
import MiniInsuranceBadge from "./MiniInsuranceBadge";
import AuctionTimer from "./AuctionTimer";
import { useCompare } from "../lib/compare";
import BrandLogo from "./brand/BrandLogo";
import { toast } from "sonner";
import { normalizeImageUrl } from "../lib/imageUrl";

export default function ListingCard({ listing, onToggleFavorite, isFavorited, compact = false, vertical = false }) {
    const { t } = useTranslation();
    const owner = listing.owner;
    const isB2B = owner?.role === "b2b";
    const isAuction = listing.sale_type === "auction";
    const isPawn = listing.sale_type === "pawn";
    const compare = useCompare();
    const inCompare = compare.has(listing.id);

    const toggleCompare = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const r = compare.toggle(listing.id);
        if (r && r.full) {
            toast.error("Maximal 3 Fahrzeuge im Vergleich");
            return;
        }
        if (inCompare) {
            toast("Aus Vergleich entfernt");
        } else {
            toast.success("Zum Vergleich hinzugefügt");
        }
    };
    const img = normalizeImageUrl(listing.images?.[0]) || "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=800";
    const displayPrice = isAuction
        ? (listing.current_bid || listing.starting_price)
        : isPawn
            ? listing.pawn_payout_eur
            : listing.price;
    const auctionEndMs = isAuction && listing.auction_end ? new Date(listing.auction_end).getTime() : null;
    const msLeft = auctionEndMs ? auctionEndMs - Date.now() : null;
    const endingSoon = msLeft != null && msLeft > 0 && msLeft < 24 * 3600 * 1000;

    return (
        <Link
            to={`/listing/${listing.id}`}
            className={`group flex flex-col bg-white border border-gray-200 hover:border-[#111827] hover:shadow-lg transition-all rounded-sm overflow-hidden ${vertical ? "h-full" : "md:flex-row"}`}
            data-testid="listing-card"
         target="_blank" rel="noopener noreferrer">
            <div className={`relative flex-shrink-0 bg-gray-100 overflow-hidden ${vertical ? "w-full" : "md:w-60 lg:w-64"}`}>
                <div className={vertical ? "aspect-[16/10]" : "aspect-[16/10] md:aspect-[4/3]"}>
                    <img
                        src={img}
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                    />
                </div>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggleFavorite?.(listing.id);
                    }}
                    className={`absolute top-3 right-3 w-9 h-9 rounded-sm flex items-center justify-center transition-colors ${
                        isFavorited ? "bg-[#FFCC00] text-[#111827]" : "bg-white/90 hover:bg-white text-gray-700"
                    }`}
                    data-testid="favorite-btn"
                    aria-label={isFavorited ? "Aus Merkliste entfernen" : "Zur Merkliste hinzufügen"}
                    aria-pressed={isFavorited}
                >
                    <Heart className="w-4 h-4" fill={isFavorited ? "currentColor" : "none"} aria-hidden="true" />
                </button>
                <button
                    onClick={toggleCompare}
                    className={`absolute top-14 right-3 w-9 h-9 rounded-sm flex items-center justify-center transition-colors ${
                        inCompare ? "bg-[#2563EB] text-white" : "bg-white/90 hover:bg-white text-gray-700"
                    }`}
                    title={inCompare ? "Aus Vergleich entfernen" : "Zum Vergleich hinzufügen"}
                    data-testid="compare-btn"
                    aria-label={inCompare ? "Aus Vergleich entfernen" : "Zum Vergleich hinzufügen"}
                    aria-pressed={inCompare}
                >
                    <GitCompareArrows className="w-4 h-4" aria-hidden="true" />
                </button>
                {listing.images?.length > 1 && (
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-mono-num px-2 py-1 rounded-sm">
                        {t("listing_card.photos_count", { count: listing.images.length })}
                    </div>
                )}
                <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5 z-10">
                    {isAuction && (
                        <div className="bg-[#FFCC00] text-[#111827] text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm flex items-center gap-1" data-testid="auction-badge">
                            <Gavel className="w-3 h-3" /> Auktion
                        </div>
                    )}
                    {isPawn && (
                        <div className="bg-[#7C3AED] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm flex items-center gap-1" data-testid="pawn-badge">
                            <Coins className="w-3 h-3" /> Pfand
                        </div>
                    )}
                    {isAuction && listing.auction_end && (
                        <div className="bg-white/95 backdrop-blur-sm rounded-sm px-2 py-1 shadow-md" data-testid="auction-card-countdown">
                            <AuctionTimer endIso={listing.auction_end} compact />
                        </div>
                    )}
                    {endingSoon && (
                        <div className="bg-[#DC2626] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm shadow-md inline-flex items-center gap-1 animate-pulse" data-testid="auction-ending-soon">
                            <Flame className="w-3 h-3" /> Gleich vorbei!
                        </div>
                    )}
                    {listing.is_top && (
                        <div className="bg-[#DC2626] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm shadow-md" data-testid="top-badge">
                            ★ TOP
                        </div>
                    )}
                    {/* Feb-2026 (User-Wunsch) — Archivierte Inserate bleiben in der
                        Suche sichtbar und werden klar gekennzeichnet. */}
                    {listing.status === "archived" && (
                        <div className="bg-gray-800 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm shadow-md inline-flex items-center gap-1" data-testid="archived-badge">
                            <Archive className="w-3 h-3" /> Archivierung
                        </div>
                    )}
                    {listing.price_reference?.is_reduction && (
                        <div className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm shadow-md inline-flex items-center gap-1" data-testid="price-drop-badge">
                            ↓ Preis gesenkt
                        </div>
                    )}
                </div>
                {/* Phase 3.90 — Transparenter Unfallfahrzeug-Hinweis */}
                {listing.is_accident_vehicle === true && (
                    <div
                        className="absolute top-3 right-3 bg-gray-700/95 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm shadow-md inline-flex items-center gap-1"
                        title="Unfallfahrzeug — Details in der Anzeige"
                        data-testid="accident-vehicle-badge"
                    >
                        <Info className="w-3 h-3" /> Unfallfahrzeug
                    </div>
                )}
                {typeof listing.match_score === "number" && listing.match_score > 0 && (
                    <div
                        className={`absolute bottom-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm shadow-md ${
                            listing.match_score >= 80
                                ? "bg-green-600 text-white"
                                : listing.match_score >= 60
                                ? "bg-[#FFCC00] text-[#111827]"
                                : listing.match_score >= 40
                                ? "bg-orange-400 text-white"
                                : "bg-gray-500 text-white"
                        }`}
                        data-testid="listing-card-match-score"
                        title={`Treffergrad zu Ihren gespeicherten Suchen: ${listing.match_score}%`}
                    >
                        <Target className="w-2.5 h-2.5" />
                        {Math.round(listing.match_score)}% Match
                    </div>
                )}
            </div>

            <div className={`flex-1 flex flex-col ${vertical ? "p-3" : "p-4 md:p-5"}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <BrandLogo brand={listing.brand} size={20} data-testid="card-brand-logo" />
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                {listing.brand}
                            </span>
                            <span className="text-gray-300">·</span>
                            <span className="text-xs font-semibold text-gray-500">
                                {listing.category === "motorcycle" ? "Motorrad" : "Fahrrad"}
                            </span>
                            {/* Feb-2026 (User-Wunsch 24 May Iter 15): Angebotsnummer
                                überall sichtbar wo das Fahrzeug erscheint —
                                einmalig + unveränderlich. */}
                            {listing.internal_number && (
                                <span
                                    className="ml-auto inline-flex items-center gap-1 bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm font-mono-num text-[10px] font-bold tracking-wider shrink-0"
                                    title="Einmalige und unveränderliche Angebotsnummer"
                                    data-testid="listing-card-internal-number"
                                >
                                    Nr. {listing.internal_number}
                                </span>
                            )}
                        </div>
                        <h3 className="font-display text-lg md:text-xl font-bold text-[#111827] leading-tight line-clamp-2" data-testid="listing-title">
                            {listing.title}
                        </h3>
                    </div>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-gray-700 mb-3">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-400" /> EZ {listing.year}</span>
                    {listing.category === "motorcycle" ? (
                        <>
                            <span className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-gray-400" /> {formatMileage(listing.mileage)}</span>
                            {listing.power_hp && <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-gray-400" /> {listing.power_hp} PS</span>}
                        </>
                    ) : (
                        <>
                            {listing.bike_type && <span className="font-medium">{listing.bike_type}</span>}
                            {listing.frame_size && <span>Gr. {listing.frame_size}</span>}
                        </>
                    )}
                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {listing.location}</span>
                </div>

                {!compact && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4">{listing.description}</p>
                )}

                <div className={`mt-auto pt-3 border-t border-gray-100 gap-3 ${vertical ? "flex flex-col-reverse" : "flex items-end justify-between"}`}>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                        <Badge
                            className={
                                isB2B
                                    ? "bg-[#111827] text-white hover:bg-[#111827] uppercase tracking-wide font-bold rounded-sm"
                                    : "bg-gray-200 text-gray-800 hover:bg-gray-200 uppercase tracking-wide font-bold rounded-sm"
                            }
                            data-testid="seller-badge"
                        >
                            {isB2B ? t("dealers.badge_dealer") : t("dealers.badge_private")}
                        </Badge>
                        {isB2B && owner?.verified && (
                            <Badge
                                className="bg-green-100 text-green-800 hover:bg-green-100 gap-1 rounded-sm uppercase tracking-wider text-[9px] font-bold"
                                data-testid="verified-badge"
                                title={t("listing_card.verified_dealer_tooltip")}
                            >
                                <ShieldCheck className="w-3 h-3" /> ✓
                            </Badge>
                        )}
                        <span className="text-gray-600 truncate max-w-[150px]">{owner?.company_name || owner?.name}</span>
                        {isB2B && owner?.rating_count > 0 && (
                            <span
                                className="inline-flex items-center gap-0.5 text-xs text-gray-700"
                                data-testid="listing-card-rating"
                                title={`${owner.rating_avg} ★ aus ${owner.rating_count} Bewertung${owner.rating_count === 1 ? "" : "en"}`}
                            >
                                <Star className="w-3 h-3 fill-[#FFCC00] text-[#FFCC00]" />
                                <span className="font-mono-num font-semibold">{owner.rating_avg}</span>
                                <span className="text-gray-500">({owner.rating_count})</span>
                            </span>
                        )}
                        {/* Phase 3.34 — "Antwortet schnell"-Badge auch im Such-Ergebnis */}
                        {isB2B && owner?.responds_fast && (
                            <span
                                className="hidden sm:inline-flex items-center gap-0.5 bg-green-100 text-green-800 border border-green-300 px-1 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                                data-testid="listing-card-fast-responder"
                                title={`Reagiert im Schnitt in ${owner.response_avg_hours ?? "?"} h auf Bewertungen`}
                            >
                                <MessageSquare className="w-2.5 h-2.5" /> Schnell
                            </span>
                        )}
                    </div>
                    <div className={vertical ? "text-left" : "text-right"}>
                        {isAuction && (
                            <div className={`text-[10px] uppercase tracking-wider font-bold mb-0.5 ${listing.current_bid ? "text-[#DC2626]" : "text-gray-500"}`}>
                                {listing.current_bid ? "Höchstgebot" : "Startpreis"}
                            </div>
                        )}
                        {isPawn && (
                            <div className="text-[10px] uppercase tracking-wider font-bold mb-0.5 text-[#7C3AED]">
                                Auszahlung
                            </div>
                        )}
                        <div className="font-display font-black text-2xl text-[#111827] font-mono-num leading-none" data-testid="listing-price">
                            {formatPrice(displayPrice)}
                        </div>
                        {!isAuction && !isPawn && listing.price_reference?.is_reduction && (
                            <div className={`mt-1 flex items-center gap-1.5 ${vertical ? "justify-start" : "justify-end"}`} data-testid="listing-price-reduction">
                                <span className="text-xs text-gray-500 line-through font-mono-num">{formatPrice(listing.price_reference.lowest_30d)}</span>
                                <span className="inline-flex items-center rounded-sm bg-red-100 text-red-700 px-1 py-0.5 text-[10px] font-bold">−{listing.price_reference.savings_pct}%</span>
                            </div>
                        )}
                        {isAuction ? (
                            <div className={`mt-1.5 flex flex-col gap-0.5 ${vertical ? "items-start" : "items-end"}`}>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500">
                                    {listing.current_bid ? `${listing.bid_count || 0} Gebote` : "Noch keine Gebote"}
                                </div>
                                <AuctionTimer endIso={listing.auction_end} compact />
                            </div>
                        ) : isPawn ? (
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">
                                {listing.pawn_term_months ? `${listing.pawn_term_months} Mon. Laufzeit` : "Pfand / Refinanzieren"}
                                {listing.pawn_buyback_eur ? ` · Rückkauf ${formatPrice(listing.pawn_buyback_eur)}` : ""}
                            </div>
                        ) : (
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{t("listing_card.vat_included")}</div>
                        )}
                        {/* Phase 4.22 — Mini-Insurance-Badge */}
                        <div className={`mt-1.5 flex ${vertical ? "justify-start" : "justify-end"}`}>
                            <MiniInsuranceBadge listing={listing} />
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );
}
